import React from 'react';

interface RoundTableWithInvoice {
    id: number;
    name: string;
    number: number;
    billingEmail: string;
    selected: boolean;
}

interface EmailResult {
    roundTableId: number;
    roundTableName?: string;
    success: boolean;
    error?: string;
    attachmentWarning?: string;
}

interface SendResults {
    message: string;
    totalRecipients: number;
    successCount: number;
    failureCount: number;
    warningsCount?: number;
    results: EmailResult[];
}

interface EmailWizardStep3Props {
    roundTablesWithInvoices: RoundTableWithInvoice[];
    subject: string;
    cc: string;
    content: string;
    attachInvoice: boolean;
    onSendTest: () => void;
    onSkipTest: () => void;
    isSending: boolean;
    sendProgress: { current: number; total: number };
    sendResults: SendResults | null;
}

const EmailWizardStep3: React.FC<EmailWizardStep3Props> = ({
    roundTablesWithInvoices,
    subject,
    cc,
    content,
    attachInvoice,
    onSendTest,
    onSkipTest,
    isSending,
    sendProgress,
    sendResults,
}) => {
    const selectedTables = roundTablesWithInvoices.filter(t => t.selected);
    const randomTable = selectedTables.length > 0 
        ? selectedTables[Math.floor(Math.random() * selectedTables.length)]
        : null;

    // Replace placeholders for example
    // Note: This is for preview only. Actual implementation would fetch real member counts
    // and use the actual bank account information from the round table data
    const replacePlaceholders = (text: string, table: RoundTableWithInvoice | null) => {
        if (!table) return text;
        
        return text
            .replace(/\{\{name\}\}/g, table.name)
            .replace(/\{\{tableNumber\}\}/g, table.number.toString())
            .replace(/\{\{memberCount\}\}/g, '0') // TODO: Fetch actual member count from backend
            .replace(/\{\{bankAccount\}\}/g, 'IBAN: DE12345678901234567890'); // TODO: Use actual bank account from round table data
    };

    const exampleSubject = randomTable ? replacePlaceholders(subject, randomTable) : subject;
    const exampleContent = randomTable ? replacePlaceholders(content, randomTable) : content;

    return (
        <div className="project-email-wizard-step">
            <h3>Überprüfung</h3>
            
            {/* Summary */}
            <div className="project-email-email-summary">
                <div className="summary-item">
                    <strong>Anzahl der E-Mails:</strong> {selectedTables.length}
                </div>
                {attachInvoice && (
                    <div className="project-email-summary-item">
                        <strong>Anhang:</strong> Rechnung als PDF
                    </div>
                )}
            </div>

            {/* Example Email */}
            {randomTable && (
                <div className="project-email-email-preview">
                    <h4 style={{ marginTop: 0, marginBottom: '16px', color: '#333' }}>Beispiel E-Mail:</h4>
                    
                    <div className="project-email-email-preview-field">
                        <strong>An:</strong> {randomTable.billingEmail || 'keine E-Mail hinterlegt'}
                    </div>
                    
                    {cc && (
                        <div className="project-email-email-preview-field">
                            <strong>CC:</strong> {cc}
                        </div>
                    )}
                    
                    <div className="project-email-email-preview-field">
                        <strong>Betreff:</strong> {exampleSubject}
                    </div>
                    
                    <div className="project-email-email-preview-body">
                        <strong>Inhalt:</strong>
                        <div style={{ 
                            marginTop: '8px', 
                            padding: '12px', 
                            backgroundColor: '#fff', 
                            border: '1px solid #e9ecef',
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit'
                        }}>
                            {exampleContent}
                        </div>
                    </div>
                </div>
            )}

            {selectedTables.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6c757d' }}>
                    Keine Round Tables ausgewählt
                </div>
            )}

            {/* Action Buttons */}
            {selectedTables.length > 0 && (
                <div className="project-email-email-test-actions">
                    <button
                        type="button"
                        className="wizard-btn project-email-wizard-btn-test"
                        onClick={onSendTest}
                    >
                        Test-E-Mail senden
                    </button>
                    <button
                        type="button"
                        className="wizard-btn project-email-wizard-btn-skip"
                        onClick={onSkipTest}
                    >
                        Test überspringen
                    </button>
                </div>
            )}

            {/* Sending Progress */}
            {isSending && (
                <div className="email-sending-progress">
                    <h4>E-Mails werden versendet...</h4>
                    <div className="progress-bar">
                        <div 
                            className="progress-bar-fill" 
                            style={{ width: `${(sendProgress.current / sendProgress.total) * 100}%` }}
                        ></div>
                    </div>
                    <p className="progress-text">
                        {sendProgress.current} von {sendProgress.total} E-Mails versendet
                    </p>
                </div>
            )}

            {/* Results Summary */}
            {sendResults && !isSending && (
                <div className="email-results">
                    <h4>Ergebnis</h4>
                    <div className="results-summary">
                        <div className="result-item success">
                            <strong>Erfolgreich:</strong> {sendResults.successCount}
                        </div>
                        {sendResults.failureCount > 0 && (
                            <div className="result-item failure">
                                <strong>Fehlgeschlagen:</strong> {sendResults.failureCount}
                            </div>
                        )}
                        {sendResults.warningsCount && sendResults.warningsCount > 0 && (
                            <div className="result-item warning" style={{ color: '#ff9800' }}>
                                <strong>Warnungen:</strong> {sendResults.warningsCount} Anhang(e) konnten nicht hinzugefügt werden
                            </div>
                        )}
                    </div>
                    {sendResults.results && sendResults.results.some((r) => r.attachmentWarning) && (
                        <div className="warning-emails" style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                            <h5 style={{ marginTop: 0, color: '#856404' }}>Anhänge nicht hinzugefügt:</h5>
                            <ul style={{ marginBottom: 0, color: '#856404' }}>
                                {sendResults.results
                                    .filter((r) => r.attachmentWarning)
                                    .map((r, idx: number) => (
                                        <li key={idx}>
                                            <strong>{r.roundTableName || `RT ${r.roundTableId}`}:</strong> {r.attachmentWarning}
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    )}
                    {sendResults.results && sendResults.results.some((r) => !r.success) && (
                        <div className="failed-emails">
                            <h5>Fehlgeschlagene E-Mails:</h5>
                            <ul>
                                {sendResults.results
                                    .filter((r) => !r.success)
                                    .map((r, idx: number) => (
                                        <li key={idx}>
                                            <strong>{r.roundTableName || `RT ${r.roundTableId}`}:</strong> {r.error}
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmailWizardStep3;
